// Web Audio API sound synthesizer for Darts application (Offline compatible)
import { tf, type Language } from './i18n';

const VOICE_LANG: Record<Language, string> = { nl: 'nl-NL', en: 'en-US' };

class AudioEngine {
  private ctx: AudioContext | null = null;
  private soundEnabled: boolean = true;

  constructor() {
    // Lazy init context on first user interaction
  }

  private initContext() {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
  }

  public setEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
  }

  public isEnabled(): boolean {
    return this.soundEnabled;
  }

  // Play standard dart hit sound
  public playDartHit(multiplier: 1 | 2 | 3 = 1) {
    if (!this.soundEnabled) return;
    this.initContext();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    
    // Thump sound (dart hitting board)
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    const startFreq = multiplier === 3 ? 420 : multiplier === 2 ? 340 : 260;
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + 0.08);

    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start(now);
    osc.stop(now + 0.08);

    // Subtle click/thud noise
    if (multiplier > 1) {
      setTimeout(() => {
        if (!this.ctx) return;
        const chimeOsc = this.ctx.createOscillator();
        const chimeGain = this.ctx.createGain();
        chimeOsc.type = 'triangle';
        chimeOsc.frequency.setValueAtTime(multiplier === 3 ? 880 : 660, this.ctx.currentTime);
        chimeGain.gain.setValueAtTime(0.2, this.ctx.currentTime);
        chimeGain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.12);
        chimeOsc.connect(chimeGain);
        chimeGain.connect(this.ctx.destination);
        chimeOsc.start();
        chimeOsc.stop(this.ctx.currentTime + 0.12);
      }, 30);
    }
  }

  // Speak custom text using Web Speech API
  public speakText(text: string, lang: string = 'en-US') {
    if (!this.soundEnabled) return;
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.rate = 1.0;
        window.speechSynthesis.speak(utterance);
      } catch {
        // ignore
      }
    }
  }

  // Speak score out loud using Web Speech API
  public speakScore(score: number, isBust: boolean = false, language: Language = 'nl') {
    if (!this.soundEnabled) return;
    if (score === 180) {
      this.speak180();
      return;
    }
    this.speakText(isBust ? 'Bust!' : `${score}`, VOICE_LANG[language]);
  }

  // Speak both turn score and remaining intermediate score for the player
  public speakTurnScore(
    turnScore: number,
    remainingScore: number,
    isBust: boolean = false,
    language: Language = 'nl',
    isGameWon: boolean = false,
    playerName?: string,
    gameMode?: string
  ) {
    if (!this.soundEnabled) return;
    if (isGameWon) return;

    const vars = { prefix: playerName ? `${playerName}: ` : '', n: remainingScore, score: turnScore };
    const key = isBust
      ? 'speechBust'
      : turnScore === 180
      ? 'speech180'
      : gameMode === 'countup' || gameMode === 'cricket'
      ? 'speechScoredPoints'
      : 'speechScoredLeft';
    this.speakText(tf(language, key, vars), VOICE_LANG[language]);
  }

  public speakTriple20() {
    this.speakText('Triple twenty!', 'en-US');
  }

  public speakBull(isDouble: boolean = false) {
    this.speakText(isDouble ? 'Double bull!' : 'Bull!', 'en-US');
  }

  public speak180() {
    this.speakText('One hundred and eighty!', 'en-US');
  }

  public speakWinner(name: string, language: Language = 'nl') {
    this.speakText(tf(language, 'speechWinner', { name }), VOICE_LANG[language]);
  }

  // Play 180 Fanfare
  public play180() {
    if (!this.soundEnabled) return;
    this.initContext();
    if (!this.ctx) return;

    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const now = this.ctx.currentTime;

    notes.forEach((freq, idx) => {
      if (!this.ctx) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + idx * 0.1);

      gain.gain.setValueAtTime(0, now + idx * 0.1);
      gain.gain.linearRampToValueAtTime(0.3, now + idx * 0.1 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.1 + 0.3);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now + idx * 0.1);
      osc.stop(now + idx * 0.1 + 0.3);
    });
  }

  // Play Bust sound
  public playBust() {
    if (!this.soundEnabled) return;
    this.initContext();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.linearRampToValueAtTime(90, now + 0.25);

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start(now);
    osc.stop(now + 0.25);
  }

  // Play Victory / Leg Win Fanfare
  public playVictory() {
    if (!this.soundEnabled) return;
    this.initContext();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const notes = [440, 554.37, 659.25, 880]; // A4, C#5, E5, A5
    notes.forEach((freq, idx) => {
      if (!this.ctx) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + idx * 0.12);

      gain.gain.setValueAtTime(0.35, now + idx * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.12 + 0.4);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now + idx * 0.12);
      osc.stop(now + idx * 0.12 + 0.4);
    });
  }
}

export const audioEngine = new AudioEngine();
